/**
 * Minimal WASI preview1 shim for the chores Tier-2 wasm module.
 *
 * TinyGo's `-target wasi` emit imports a small set of preview1
 * functions even when the guest doesn't use any I/O. The complete
 * set we observe in `core.wasm`:
 *
 *   proc_exit, fd_write, fd_read, fd_close, fd_seek,
 *   fd_fdstat_get, fd_prestat_get, fd_prestat_dir_name,
 *   path_open, clock_time_get, random_get,
 *   args_get, args_sizes_get,
 *   environ_get, environ_sizes_get
 *
 * Only `clock_time_get`, `random_get`, `fd_write` (for panic
 * stderr), and `proc_exit` (to halt on a TinyGo panic) need real
 * implementations; everything else returns success-with-nothing
 * or BADF so the guest's startup probes terminate cleanly.
 *
 * Mirrors the WasmKitWASI bridge used by iOS's WasmHost.swift,
 * but trimmed to the surface our guest actually touches.
 */

const ERRNO_SUCCESS = 0;
const ERRNO_BADF = 8;

const CLOCKID_REALTIME = 0;
const CLOCKID_MONOTONIC = 1;

const NANOS_PER_MS = 1_000_000n;
const NANOS_PER_SEC = 1_000_000_000n;

export class WasiExit extends Error {
  constructor(public readonly code: number) {
    super(`WASI proc_exit(${code})`);
    this.name = 'WasiExit';
  }
}

export type WasiImports = Record<string, (...args: number[]) => number>;

export function createWasiImports(getMemory: () => WebAssembly.Memory): WasiImports {
  const view = (): DataView => new DataView(getMemory().buffer);
  const bytes = (): Uint8Array => new Uint8Array(getMemory().buffer);

  // Panic / stderr writes get collected per-fd and flushed line by
  // line to the console. Buffering matters because TinyGo emits
  // multi-write panics (each chunk a separate fd_write call).
  const fdBuffers = new Map<number, string>();
  const decoder = new TextDecoder('utf-8', { fatal: false });

  function flushFd(fd: number, channel: 'log' | 'error'): void {
    const buf = fdBuffers.get(fd);
    if (!buf) return;
    let idx: number;
    let rest = buf;
    while ((idx = rest.indexOf('\n')) !== -1) {
      const line = rest.slice(0, idx);
      if (line.length > 0) {
        if (channel === 'error') console.error(`[wasm fd=${fd}] ${line}`);
        else console.log(`[wasm fd=${fd}] ${line}`);
      }
      rest = rest.slice(idx + 1);
    }
    fdBuffers.set(fd, rest);
  }

  return {
    // proc_exit(code: i32) → never
    proc_exit(code: number): number {
      throw new WasiExit(code);
    },

    // fd_write(fd: i32, iovs_ptr: i32, iovs_len: i32, nwritten_ptr: i32) → errno
    fd_write(fd: number, iovsPtr: number, iovsLen: number, nwrittenPtr: number): number {
      const dv = view();
      let written = 0;
      const channel: 'log' | 'error' = fd === 1 ? 'log' : 'error';
      for (let i = 0; i < iovsLen; i++) {
        const ptr = dv.getUint32(iovsPtr + i * 8, true);
        const len = dv.getUint32(iovsPtr + i * 8 + 4, true);
        if (len === 0) continue;
        const slice = bytes().subarray(ptr, ptr + len);
        const text = decoder.decode(slice);
        fdBuffers.set(fd, (fdBuffers.get(fd) ?? '') + text);
        written += len;
      }
      flushFd(fd, channel);
      dv.setUint32(nwrittenPtr, written, true);
      return ERRNO_SUCCESS;
    },

    // fd_read(fd, iovs_ptr, iovs_len, nread_ptr) → errno
    fd_read(_fd: number, _iovsPtr: number, _iovsLen: number, nreadPtr: number): number {
      view().setUint32(nreadPtr, 0, true);
      return ERRNO_SUCCESS;
    },

    fd_close(_fd: number): number {
      return ERRNO_SUCCESS;
    },

    // fd_seek(fd, offset:i64, whence, newoffset_ptr) → errno
    // TinyGo's lookup arity is (fd, offset_lo, offset_hi, whence, newoffset_ptr) for i64-as-two-i32 ABI.
    fd_seek(): number {
      return ERRNO_SUCCESS;
    },

    fd_fdstat_get(_fd: number, _statPtr: number): number {
      return ERRNO_SUCCESS;
    },

    // fd_prestat_get returns BADF to terminate the guest's
    // "iterate preopened fds" loop on startup. Without this the
    // module spins polling the fd table forever.
    fd_prestat_get(_fd: number, _bufPtr: number): number {
      return ERRNO_BADF;
    },

    fd_prestat_dir_name(_fd: number, _pathPtr: number, _pathLen: number): number {
      return ERRNO_BADF;
    },

    // path_open is referenced by TinyGo's wasi syscall layer even
    // when no file I/O runs at the Go level. We never want to
    // grant fs access, so always refuse with BADF — the guest's
    // os.Open paths would surface as Go errors, but our Tier-2
    // exports don't touch the filesystem.
    path_open(): number {
      return ERRNO_BADF;
    },

    // clock_time_get(id: i32, precision: i64, time_ptr: i32) → errno
    clock_time_get(id: number, _precisionLo: number, _precisionHi: number, timePtr: number): number {
      let ns: bigint;
      if (id === CLOCKID_REALTIME) {
        ns = BigInt(Date.now()) * NANOS_PER_MS;
      } else if (id === CLOCKID_MONOTONIC) {
        ns = BigInt(Math.round(performance.now() * 1e6));
      } else {
        ns = 0n;
      }
      view().setBigUint64(timePtr, ns, true);
      return ERRNO_SUCCESS;
    },

    // random_get(buf_ptr, buf_len) → errno
    random_get(bufPtr: number, bufLen: number): number {
      const out = bytes().subarray(bufPtr, bufPtr + bufLen);
      crypto.getRandomValues(out);
      return ERRNO_SUCCESS;
    },

    args_get(_argv: number, _argvBuf: number): number {
      return ERRNO_SUCCESS;
    },

    args_sizes_get(argcPtr: number, argvBufSizePtr: number): number {
      const dv = view();
      dv.setUint32(argcPtr, 0, true);
      dv.setUint32(argvBufSizePtr, 0, true);
      return ERRNO_SUCCESS;
    },

    environ_get(_envPtr: number, _envBufPtr: number): number {
      return ERRNO_SUCCESS;
    },

    environ_sizes_get(envCountPtr: number, envBufSizePtr: number): number {
      const dv = view();
      dv.setUint32(envCountPtr, 0, true);
      dv.setUint32(envBufSizePtr, 0, true);
      return ERRNO_SUCCESS;
    },

    // Ignore unknown WASI ABI quirks we don't service — leave the
    // suffix-mismatch fallback to the WebAssembly engine, which
    // surfaces as a clear LinkError at instantiate time instead of
    // a silent miss at call time.
  };
}

/**
 * NANOS_PER_SEC is exported for callers that need to convert
 * wasm-returned timestamps back to seconds.
 */
export const WASI_NANOS_PER_SEC = NANOS_PER_SEC;
